<template>
  <div class="prof-root">
    <!-- Snackbar -->
    <v-snackbar v-model="showSnackBar" :color="snackbarColor" top right rounded="pill" :timeout="3500">
      <div style="display:flex;align-items:center;gap:8px;">
        <v-icon small left>{{ snackbarColor === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle' }}</v-icon>
        {{ msg }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" small @click="showSnackBar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <vue-element-loading :active="appLoading" :is-full-screen="true" background-color="rgba(231,241,240,0.8)" color="#13736f" spinner="spinner" />

    <div class="prof-layout">
      <!-- LEFT: User card -->
      <div class="prof-sidebar">
        <div class="prof-card user-card">
          <!-- Avatar -->
          <div class="ava-ring">
            <div class="ava-inner">
              <span class="ava-letter">{{ avatarInitial }}</span>
            </div>
          </div>
          <div class="ava-pulse"></div>

          <h2 class="ava-name">{{ Profiledata.username || '—' }}</h2>
          <div class="ava-badge">
            <v-icon x-small color="#13736f">mdi-shield-account</v-icon>
            {{ Profiledata.role || roleLabel }}
          </div>

          <div class="ava-divider"></div>

          <div class="ava-info-list">
            <div class="ava-info-row" v-if="Profiledata.username">
              <div class="ava-info-icon"><v-icon small color="#13736f">mdi-account-outline</v-icon></div>
              <div class="ava-info-body">
                <span class="ava-info-lbl">Full Name</span>
                <span class="ava-info-val">{{ Profiledata.username }}</span>
              </div>
            </div>
            <div class="ava-info-row" v-if="Profiledata.officeName">
              <div class="ava-info-icon"><v-icon small color="#13736f">mdi-office-building-outline</v-icon></div>
              <div class="ava-info-body">
                <span class="ava-info-lbl">{{ isClerk ? 'Section Name' : 'Office' }}</span>
                <span class="ava-info-val">{{ Profiledata.officeName }}</span>
              </div>
            </div>
            <div class="ava-info-row" v-if="Profiledata.role">
              <div class="ava-info-icon"><v-icon small color="#13736f">mdi-shield-account-outline</v-icon></div>
              <div class="ava-info-body">
                <span class="ava-info-lbl">Designation</span>
                <span class="ava-info-val">{{ Profiledata.role }}</span>
              </div>
            </div>
            <div class="ava-info-row" v-if="Profiledata.emailId">
              <div class="ava-info-icon"><v-icon small color="#13736f">mdi-email-outline</v-icon></div>
              <div class="ava-info-body">
                <span class="ava-info-lbl">Email</span>
                <span class="ava-info-val">{{ Profiledata.emailId }}</span>
              </div>
            </div>
            <div class="ava-info-row" v-if="Profiledata.mobNumber">
              <div class="ava-info-icon"><v-icon small color="#13736f">mdi-phone-outline</v-icon></div>
              <div class="ava-info-body">
                <span class="ava-info-lbl">Mobile</span>
                <span class="ava-info-val">{{ Profiledata.mobNumber }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Edit form -->
      <div class="prof-main">
        <!-- Edit Form Card -->
        <div class="prof-card form-card">
          <div class="form-card-head">
            <div class="form-head-icon">
              <v-icon color="#13736f">mdi-account-outline</v-icon>
            </div>
            <div>
              <h3 class="form-head-title">Profile</h3>
            </div>
          </div>

          <div class="form-divider"></div>

          <!-- Full name & Section / Office (Editable) -->
          <div class="form-section-lbl">
            <v-icon x-small color="#13736f" style="margin-right:4px;">mdi-pencil-circle</v-icon>
            {{ isClerk ? 'Name & Section' : 'Name & Office' }}
          </div>
          <div class="form-grid">
            <div class="field-wrap" :class="{ 'field-wrap--err': !!fullNameError }">
              <label class="field-lbl">Full Name <span class="field-req">*</span></label>
              <div class="field-box" :class="{ 'field-box--err': !!fullNameError }">
                <v-icon class="field-ico" small>mdi-account-outline</v-icon>
                <input v-model="Profiledata.username" class="field-inp" type="text" placeholder="Enter full name" @input="clearFullNameError" />
              </div>
              <span v-if="fullNameError" class="field-err-msg">
                <v-icon x-small color="#dc2626">mdi-alert-circle</v-icon> {{ fullNameError }}
              </span>
            </div>

            <div class="field-wrap" :class="{ 'field-wrap--err': !!officeNameError }">
              <label class="field-lbl">{{ isClerk ? 'Section Name' : 'Office Name' }} <span class="field-req">*</span></label>
              <div class="field-box" :class="{ 'field-box--err': !!officeNameError }">
                <v-icon class="field-ico" small>mdi-office-building-outline</v-icon>
                <input v-model="Profiledata.officeName" class="field-inp" type="text" :placeholder="isClerk ? 'Enter section name' : 'Enter office name'" @input="clearOfficeNameError" />
              </div>
              <span v-if="officeNameError" class="field-err-msg">
                <v-icon x-small color="#dc2626">mdi-alert-circle</v-icon> {{ officeNameError }}
              </span>
            </div>
          </div>

          <!-- Read-only section -->
          <div class="form-section-lbl" style="margin-top:18px;">
            <v-icon x-small color="#94a3b8" style="margin-right:4px;">mdi-lock-outline</v-icon>
            Account Information
          </div>
          <div class="form-grid">
            <div class="field-wrap">
              <label class="field-lbl">Designation</label>
              <div class="field-box field-box--ro">
                <v-icon class="field-ico" small>mdi-shield-account-outline</v-icon>
                <input :value="Profiledata.role || 'Official'" class="field-inp" type="text" placeholder="—" disabled />
                <v-icon x-small style="color:#94a3b8;margin-left:4px;">mdi-lock</v-icon>
              </div>
            </div>
            <div class="field-wrap">
              <label class="field-lbl">Mobile Number</label>
              <div class="field-box field-box--ro">
                <v-icon class="field-ico" small>mdi-phone-outline</v-icon>
                <input :value="Profiledata.mobNumber" class="field-inp" type="text" placeholder="—" disabled />
                <v-icon x-small style="color:#94a3b8;margin-left:4px;">mdi-lock</v-icon>
              </div>
            </div>
            <div class="field-wrap">
              <label class="field-lbl">Email Address</label>
              <div class="field-box field-box--ro">
                <v-icon class="field-ico" small>mdi-email-outline</v-icon>
                <input :value="Profiledata.emailId" class="field-inp" type="text" placeholder="—" disabled />
                <v-icon x-small style="color:#94a3b8;margin-left:4px;">mdi-lock</v-icon>
              </div>
            </div>
          </div>

          <div class="form-divider" style="margin-top:20px;"></div>

          <!-- Actions -->
          <div class="form-actions">
            <button class="btn-save" @click="validateAndSaveProfile" :disabled="appLoading">
              <v-icon small style="margin-right:4px;color:#fff;">mdi-content-save-outline</v-icon>Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { getUserType, isSuperAdmin, panelLabels } from "@/components/Common/navConfig";
import { setStoredUsername } from "@/utils/userName";
export default {
  name: "ProfilePage",
  data() {
    return {
      msg: null,
      showSnackBar: false,
      snackbarColor: "success",
      ServerError: false,
      appLoading: false,
      Profiledata: {},
      officeNameError: "",
      fullNameError: "",
    };
  },
  computed: {
    avatarInitial() {
      const n = this.Profiledata.username || this.Profiledata.emailId || "U";
      return n.charAt(0).toUpperCase();
    },
    isClerk() {
      const u = localStorage.getItem("uType");
      return u === "7741" || this.Profiledata.uType === 7741 || getUserType() === "clerk";
    },
    roleLabel() {
      if (isSuperAdmin()) return "Super Admin";
      return this.Profiledata.role || localStorage.getItem("role") || panelLabels[getUserType()];
    },
  },
  mounted() { this.getProfile(); },
  methods: {
    getProfile() {
      this.appLoading = true;
      axios({ method: "GET", url: "/profile", headers: { token: localStorage.getItem("token") } })
        .then((r) => { this.Profiledata = r.data.data; this.appLoading = false; })
        .catch((e) => { this.appLoading = false; this.showErr("Failed to load profile data"); console.log(e); });
    },
    validateAndSaveProfile() {
      this.officeNameError = "";
      this.fullNameError = "";
      if (!this.Profiledata.username?.trim()) {
        this.fullNameError = "Full name is required";
        this.showErr("Please enter your full name");
        return;
      }
      if (!this.Profiledata.officeName?.trim()) {
        this.officeNameError = (this.isClerk ? "Section name" : "Office name") + " is required";
        this.showErr("Please enter " + (this.isClerk ? "section name" : "office name"));
        return;
      }
      this.saveProfile();
    },
    clearOfficeNameError() { this.officeNameError = ""; },
    clearFullNameError() { this.fullNameError = ""; },
    showErr(m) { this.msg = m; this.snackbarColor = "error"; this.showSnackBar = true; },
    saveProfile() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/edit-user-profile",
        headers: { token: localStorage.getItem("token") },
        data: {
          username: this.Profiledata.username.trim(),
          officeName: this.Profiledata.officeName.trim(),
        },
      })
        .then((r) => {
          if (r.data.status !== false) {
            // Keep the header, sidebar and dashboard greeting in sync without re-login
            setStoredUsername(r.data.data?.username || this.Profiledata.username);
            this.msg = r.data.msg || "Profile updated successfully";
            this.snackbarColor = "success";
            this.showSnackBar = true;
            this.getProfile();
          } else {
            this.showErr(r.data.msg || "Failed to update profile");
          }
          this.appLoading = false;
        })
        .catch((e) => {
          this.appLoading = false;
          this.showErr("Failed to update profile");
          console.log(e);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* ── Root ─────────────────────────────────────── */
.prof-root {
  font-family: var(--font-body);
  height: calc(100vh - 68px);
  background: #e7f1f0;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
}

/* ── Two-column layout ───────────────────────── */
.prof-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 20px;
  padding: 20px 24px;
  width: 100%;
  /* max-width: 1140px; */
  margin: 0 auto;
  align-items: stretch;
}

@media (max-width: 800px) {
  .prof-root { height: auto; overflow: auto; }
  .prof-layout { grid-template-columns: 1fr; padding: 16px; }
}

/* ── Shared card ─────────────────────────────── */
.prof-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 2px 16px rgba(12, 79, 76, 0.07);
  padding: 22px;
  border: 1px solid rgba(19, 115, 111, 0.08);
  transition: box-shadow 0.25s;
}
.prof-card:hover { box-shadow: 0 6px 28px rgba(12, 79, 76, 0.12); }

.prof-sidebar {
  display: flex;
  flex-direction: column;
}

/* ── Avatar / User card ─────────────────────── */
.user-card,
.form-card {
  height: 100%;
}

.user-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0;
  position: relative;
}

.ava-ring {
  width: 90px; height: 90px;
  border-radius: 50%;
  padding: 3px;
  background: conic-gradient(#13736f, #2a9d8f, #0c4f4c, #13736f);
  animation: spinRing 7s linear infinite;
  margin-bottom: 14px;
  flex-shrink: 0;
}
@keyframes spinRing { to { transform: rotate(360deg); } }

.ava-inner {
  width: 100%; height: 100%;
  border-radius: 50%;
  background: #f1f7f6;
  display: flex; align-items: center; justify-content: center;
}

.ava-letter { font-size: 34px; font-weight: 800; color: #13736f; line-height: 1; }

/* .ava-pulse {
  position: absolute;
  top: 22px; right: calc(50% - 50px);
  width: 14px; height: 14px;
  background: #ab8917;
  border-radius: 50%;
  border: 2px solid #fff;
} */

.ava-name { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 6px; word-break: break-word; }

.ava-badge {
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(19,115,111,0.1);
  border: 1px solid rgba(19,115,111,0.2);
  border-radius: 100px;
  padding: 3px 10px;
  font-size: 11px; font-weight: 600; color: #0c4f4c;
  margin-bottom: 16px;
}

.ava-divider { width: 100%; height: 1px; background: #e6edf0; margin-bottom: 14px; }

.ava-info-list { width: 100%; display: flex; flex-direction: column; gap: 10px; }

.ava-info-row { display: flex; align-items: flex-start; gap: 10px; }

.ava-info-icon {
  width: 28px; height: 28px;
  background: rgba(19,115,111,0.08);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.ava-info-body { display: flex; flex-direction: column; text-align: left; min-width: 0; }
.ava-info-lbl { font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
.ava-info-val { font-size: 12px; color: #334155; font-weight: 500; word-break: break-word; }

/* ── Main form column ────────────────────────── */
.prof-main { display: flex; flex-direction: column; gap: 14px; }

/* ── Form card header ────────────────────────── */
.form-card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }

.form-head-icon {
  width: 40px; height: 40px;
  background: rgba(19,115,111,0.1);
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.form-head-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 1px; }
.form-head-sub { font-size: 12px; color: #64748b; margin: 0; }

.form-divider { height: 1px; background: #e6edf0; margin-bottom: 14px; }

/* ── Section label ───────────────────────────── */
.form-section-lbl {
  display: flex; align-items: center;
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 10px;
}

/* ── Field grid ──────────────────────────────── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } }

.field-wrap { display: flex; flex-direction: column; gap: 4px; }

.field-lbl { font-size: 11px; font-weight: 600; color: #475569; }
.field-req { color: #dc2626; }

.field-box {
  display: flex; align-items: center;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 11px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.field-box:focus-within {
  border-color: #13736f;
  box-shadow: 0 0 0 3px rgba(19,115,111,0.1);
  background: #fff;
}
.field-box--err { border-color: #dc2626 !important; box-shadow: 0 0 0 3px rgba(220,38,38,0.08) !important; }
.field-box--ro { background: #f1f5f9; cursor: not-allowed; }

.field-ico { margin-right: 7px; color: #94a3b8 !important; flex-shrink: 0; }

.field-inp {
  flex: 1; background: transparent; border: none; outline: none;
  font-family: var(--font-body); font-size: 13px; color: #0f172a;
  padding: 10px 0; min-width: 0;
}
.field-inp::placeholder { color: #94a3b8; }
.field-inp:disabled { color: #94a3b8; cursor: not-allowed; }

.field-err-msg { display: flex; align-items: center; gap: 3px; font-size: 11px; color: #dc2626; font-weight: 500; }

/* ── Action buttons ──────────────────────────── */
.form-actions { display: flex; justify-content: flex-end; gap: 10px; }

.btn-reset {
  display: inline-flex; align-items: center;
  padding: 9px 18px; border-radius: 10px;
  border: 1.5px solid #e2e8f0; background: #fff;
  color: #475569; font-family: var(--font-body);
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.2s;
}
.btn-reset:hover { border-color: #13736f; color: #13736f; }

.btn-save {
  display: inline-flex; align-items: center;
  padding: 10px 22px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #0c4f4c, #13736f);
  color: #fff; font-family: var(--font-body);
  font-size: 13px; font-weight: 700; cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 4px 16px rgba(19,115,111,0.3);
}
.btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(19,115,111,0.4); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Security card ───────────────────────────── */
.sec-card { display: flex; align-items: center; gap: 12px; padding: 16px 20px; }

.sec-icon {
  width: 36px; height: 36px;
  background: rgba(19,115,111,0.1);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.sec-body { flex: 1; min-width: 0; }
.sec-title { font-size: 13px; font-weight: 700; color: #0f172a; margin: 0 0 2px; }
.sec-text { font-size: 11px; color: #64748b; margin: 0; line-height: 1.4; }

.sec-badge {
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(19,115,111,0.1);
  border: 1px solid rgba(19,115,111,0.2);
  border-radius: 100px; padding: 3px 9px;
  font-size: 11px; font-weight: 700; color: #13736f;
  white-space: nowrap; flex-shrink: 0;
}
</style>
